import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, CccisdInput, CccisdFieldWrapper, CccisdToggle } from 'cccisd-formik';
import Table from 'cccisd-graphql-table';
import { client } from 'cccisd-apollo';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';
import PropTypes from 'prop-types';

import sitesQuery from '../sites.graphql';
import supervisorsQuery from '../supervisors.graphql';
import RequiredFieldLabel from '../RequiredFieldLabel';

const CareCoordinatorForm = ({ onSubmit, initialValues, disableUsername = false }) => {
    const [supervisorOptions, setSupervisorOptions] = useState([]);

    const fetchSupervisors = async siteId => {
        if (!siteId) {
            setSupervisorOptions([]);
            return;
        }
        const response = await client.query({
            query: supervisorsQuery,
            fetchPolicy: 'network-only',
            variables: { siteId },
        });
        const supervisors = _get(response, 'data.roles.supervisorList', []);
        setSupervisorOptions(
            supervisors.map(supervisor => ({
                value: supervisor.pawn.pawnId,
                label: supervisor.user.fullNameWithUsername,
            }))
        );
    };

    useEffect(() => {
        if (initialValues.site && initialValues.site.length > 0) {
            fetchSupervisors(initialValues.site[0]);
        }
    }, []);

    const validate = values => {
        const errors = {};
        if (!values.username) {
            errors.username = 'Username (Email) is required.';
        }
        if (values.username && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.username)) {
            errors.username = 'Must be a valid email.';
        }
        if (!values.firstName) {
            errors.firstName = 'First Name is required.';
        }
        if (!values.lastName) {
            errors.lastName = 'Last Name is required.';
        }
        if (!values.site || values.site.length < 1) {
            errors.site = 'Site is required.';
        }
        if (values.site && values.site.length > 1) {
            errors.site = 'You may select only 1 Site.';
        }
        return errors;
    };

    const onHandleSubmit = async values => {
        values.site = values.site[0];
        await onSubmit(values);
    };

    const siteColumns = [
        { name: 'group.groupId', label: 'ID', hideInTable: true },
        { name: 'group.label', label: 'Site', sort: true, filter: true },
        { name: 'ancestorGroups.agency.group.label', label: 'Agency', sort: true, filter: true },
        {
            name: 'ancestorGroups.organization.group.label',
            label: 'Organization',
            sort: true,
            filter: true,
        },
        {
            name: 'ancestorGroups.collaborator.group.label',
            label: 'Collaborator',
            sort: true,
            filter: true,
        },
    ];

    return (
        <Formik onSubmit={e => onHandleSubmit(e)} validate={e => validate(e)} initialValues={initialValues}>
            {({ handleSubmit, values, setFieldValue }) => (
                <Form>
                    <Field
                        name="username"
                        component={CccisdInput}
                        label={<RequiredFieldLabel isRequired>Username (Email):</RequiredFieldLabel>}
                        disabled={disableUsername}
                    />
                    <Field
                        name="firstName"
                        component={CccisdInput}
                        label={<RequiredFieldLabel isRequired>First Name:</RequiredFieldLabel>}
                    />
                    <Field
                        name="lastName"
                        component={CccisdInput}
                        label={<RequiredFieldLabel isRequired>Last Name:</RequiredFieldLabel>}
                    />

                    <>
                        <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Page Permissions for Care Coordinator:</strong>
                            <br />
                        </div>
                        <Field name="managePageAccess" component={CccisdToggle} label="Manage Page Access" />
                        <Field name="dashboardPageAccess" component={CccisdToggle} label="Dashboard Page Access" />
                        <Field name="administerPageAccess" component={CccisdToggle} label="Administer Page Access" />
                    </>
                    <br />

                    <Field name="site" enableReinitialize>
                        {({ field, form }) => (
                            <CccisdFieldWrapper
                                field={field}
                                form={form}
                                label={<RequiredFieldLabel isRequired>Site:</RequiredFieldLabel>}
                            >
                                <Table
                                    query={sitesQuery}
                                    rowKey="group.groupId"
                                    columns={siteColumns}
                                    csvFilename="Sites.csv"
                                    showRowActions={false}
                                    rowSelectedCallback={selectedRows => {
                                        if (!_isEqual(selectedRows, field.value)) {
                                            setFieldValue('site', selectedRows);
                                            setFieldValue('supervisor', '');
                                            fetchSupervisors(selectedRows[0]);
                                        }
                                    }}
                                    selectedRows={initialValues.site}
                                    maxSelectableRows={1}
                                    rowActions={[
                                        {
                                            name: 'selectSite',
                                            title: 'Select Site',
                                            action: () => {},
                                        },
                                    ]}
                                />
                            </CccisdFieldWrapper>
                        )}
                    </Field>

                    {values.site && values.site.length === 1 && (
                        <Field name="supervisor">
                            {({ field, form }) => (
                                <CccisdFieldWrapper field={field} form={form} label="Supervisor:">
                                    <select
                                        className="form-select"
                                        value={field.value}
                                        onChange={e => {
                                            const val = e.target.value;
                                            setFieldValue('supervisor', val);

                                            // selecting a value after the field was empty ; reset
                                            if (val && !values.supervisor) {
                                                setFieldValue('receiveAlertsImmediately', false);
                                                setFieldValue('receiveAlertsDigest', true);
                                                setFieldValue('receiveNotifications', true);
                                            }

                                            if (!val) {
                                                setFieldValue('receiveAlertsImmediately', false);
                                                setFieldValue('receiveAlertsDigest', false);
                                                setFieldValue('receiveNotifications', false);
                                            }
                                        }}
                                    >
                                        <option value="">-- Please Select a Supervisor --</option>
                                        {supervisorOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </CccisdFieldWrapper>
                            )}
                        </Field>
                    )}

                    {values.supervisor && (
                        <>
                            <br />
                            <div>
                                <strong>Select notifications to send to the selected Supervisor:</strong>
                            </div>
                            <div style={{ paddingLeft: '1.5em' }}>
                                <div>
                                    <br />
                                    <strong>SMART-Alerts: Communication Preference Settings</strong>
                                    <br />
                                    <br />
                                </div>
                                <Field
                                    name="receiveAlertsImmediately"
                                    component={CccisdToggle}
                                    label="Supervisor also receives the Care Coordinator's immediate SMART-Alerts?"
                                />
                                <Field
                                    name="receiveAlertsDigest"
                                    component={CccisdToggle}
                                    label="Supervisor also receives the Care Coordinator's SMART-Alerts in Daily Digest?"
                                />

                                <div>
                                    <br />
                                    <strong>SMART-Notices: Communication Preference Settings</strong>
                                    <br />
                                    <br />
                                </div>
                                <Field
                                    name="receiveNotifications"
                                    component={CccisdToggle}
                                    label="Supervisor also receives the Care Coordinator's SMART-Notices in Weekly Digest?"
                                />
                            </div>
                        </>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1em' }}>
                        <button type="button" className="btn btn-primary" onClick={() => handleSubmit()}>
                            Submit
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

CareCoordinatorForm.propTypes = {
    onSubmit: PropTypes.func,
    initialValues: PropTypes.object,
    disableUsername: PropTypes.bool,
};

export default CareCoordinatorForm;
